/**
 * @prettier
 */

/**
 * Web Service handler to retrieve and represent a list of locations to the
 * authenticated location administrator account
 *
 * @class LocationsWS
 * 
 * @param  {object} parent
 * @param {Function} findQueuesByLocationId
 */
var LocationsWS = function (parent, getQueuesByLocationId) {
  var self = this; // Convenience reference so the context of 'this' becomes clearer

  // Predicate this Web Service handler on the Abstract class of a Web
  // Service handler. This establishes core functionality required of
  // these such handlers, which can be overwritten by methods below.
  AbstractWS.apply(this, arguments);

  self.name = "LocationsWS"; // Convenience name for debugging
  self._locations = ko.observableArray([]); // Collection of LocationModels
  self.indexedLocations = ko.observable(null); // Hashed collection of LocationModels

  this.resourceSelectionConfiguration = {};

  /**
   * Returns a collection of LocationModels that have appointments availabel
   *
   * @method locations
   * @return {Array}
   */
  self.locations = ko.computed(function () {
    return self._locations().filter(function (location) {
      return location.appointmentsAvailable();
    }, self, { deferEvaluation: true });
  });

  /**
   * Defines the string to request API services
   *
   * @method serviceURL
   * @return {String}
   */
  self.serviceURL = ko.computed(function () {
    return "/qless/api/v1/employee/locations";
  }, self, { deferEvaluation: true });

  /**
   * Returns an array of location ids at the logged-in merchant account
   *
   * @method locationIds
   * @return {Array}
   */
  self.locationIds = ko.computed(function () {
    return self._locations().reduce(function (ids, location) {
      ids.push(location.id);
      return ids;
    }, []);
  }, self, { deferEvaluation: true });

  /**
   * Subclass of the doRequest Superclass method, to display status message
   *
   * @method doRequest
   * @return {LocationsWS} Returns the object as chainable, for best practice
   */
  self.doRequest = function () {
    qcal.setStatusMessage("Requesting locations");

    AbstractWS.prototype.doRequest.apply(self, arguments);

    // Return self (LocationsWS) to be chainable, for best practice
    return self;
  };

  /**
   * Returns a location matching the specified Id
   *
   * @method getLocationById
   * @param {Integer} iId Id of the location to find
   * @return {LocationModel|Null}
   */
  self.getLocationById = function (iId) {
    if (iId) {
      // Loop over each location
      return self._locations().filter(function (loc) {
        return loc.id == iId;
      })[0];
    }

    return null;
  };

  /**
   * Returns a location containing the specified queueId
   *
   * @method getLocationByQueueId
   * @param {Integer} iQueueId Id of a queue
   * @return {LocationModel|Null}
   */
  self.getLocationByQueueId = function (iQueueId) {
    var i = 0, // Standard increment
      j = 0, // Standard increment
      oLoc = null, // LocationModel
      tLoc = null; // LocationModel

    if (iQueueId) {
      // Make sure the queueu id passed was an integer
      iQueueId = parseInt(iQueueId);

      // Loop over each location
      for (i = 0; i < self._locations().length; i++) {
        tLoc = self._locations()[i];
        // Loop over each queue at each location
        for (j = 0; j < tLoc.queues().length; j++) {
          // If a location is found with a queue id that matches
          if (tLoc.queues()[j].id == iQueueId) {
            // Set the return to that location
            oLoc = self._locations()[i];
            // Escape out of both loops
            j = i = 32768;
          }
        }
      }
    }

    return oLoc;
  };

  /**
   * This method iterates over the data returned from the service and
   * converts the individual nodes into LocationModels and then assigns
   * them to this object's locations collection.
   *
   * @method processData
   * @param {XMLString} xmlData XML data returned from the service
   * @return {LocationsWS} Returns the object as chainable, for best practice
   */
  self.processData = function (xmlData) {
    qcal.setStatusMessage("Locations received", 1000);

    // Use jQuery's find method to return a collection of nodes that
    // match the 'merchantLocation' tag
    var locationsData = $(xmlData).find("merchantLocation");
    var indexedLocations = {};

    var locationModels = []; // Temporary collection of LocationModels
    for (var i = 0; i < locationsData.length; i++) {
      // Create a new LocationModel object with the data node
      // and add it to the temporary location collection
      var locationModel = new LocationModel(locationsData[i], getQueuesByLocationId);
      locationModels.push(locationModel);
      indexedLocations[locationModel.id] = locationModel;

      setTimeout(self.kioskQueuesRequest(locationModel.id));
    }

    // Set the locations collection to the temporary collection of locations
    // This prevents successive firing of any methods or observables
    // that are bound to this collection.
    self._locations(locationModels);

    self.indexedLocations(indexedLocations);

    // Call the superclass method to perform any core actions
    AbstractWS.prototype.processData.call(self, xmlData);

    // Return self (LocationsWS) to be chainable, for best practice
    return self;
  };

  return self;
};

LocationsWS.prototype = Object.create(AbstractWS.prototype);
LocationsWS.prototype.constructor = LocationsWS;

LocationsWS.prototype.kioskQueuesRequest = function (locationId) {
  return function () {
    var kioskQueuesWS = new KioskQueuesWS(self.parent, locationId);
    kioskQueuesWS.doRequest(
      function () {
        this.resourceSelectionConfiguration[locationId] = kioskQueuesWS.locationConfigurations;
      }.bind(this)
    );
  }.bind(this);
};

/**
 *
 * @param {Number} locationId
 * @returns {Boolean}
 */
LocationsWS.prototype.allowResourceSelection = function (locationId) {

  var queueConfigurations = this.resourceSelectionConfiguration[locationId]
    ? this.resourceSelectionConfiguration[locationId]
    : {};

  var atLeastOneQueueSupportsResourceSelection = Object.keys(queueConfigurations).some(function (id) {
    return queueConfigurations[id].allowResourceSelection;
  });

  return atLeastOneQueueSupportsResourceSelection;
};

if (typeof exports !== "undefined") {
  exports.LocationsWS = LocationsWS;
}
